import { ElectricianSignOff } from '@/components/MilestonePackagePartials/ElectricianSignOff';
import { formatPtoGrantedDate } from '@/helpers/formatPtoGrantedDate';
import { getMostRecentConditionallyApprovedHistoryItem } from '@/helpers/getMostRecentConditionallyApprovedHistoryItem';
import {
  isPackageEditable,
  PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE,
  shouldShowPackageUnderReviewAlert,
} from '@/helpers/packageEditability';
import { validateDate } from '@/helpers/validateDate';
import { useConditionallyApprovedInstallFlags } from '@/hooks/useConditionallyApprovedInstallFlags';
import usePermissions from '@/hooks/usePermissions';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import { Alert, Box, toast } from '@palmetto/palmetto-components';
import { Formik, FormikValues } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  ActivationDocumentTypes,
  ActivationPackageDocumentTypes,
  DeepPartial,
  Flag,
  FlagPurposeType,
  ActivationPackage as IActivationPackage,
  MilestoneType,
  ProgramType,
  QueueHistory,
  QueueDocumentResponse,
  QueueStatus,
  QueueType,
  QuoteStatus,
  UserPermissions,
  validateMonitoringIdFormat,
} from 'types';

import { convertToISODateTimeString, DATE_FORMAT } from '../../helpers/dates';
import { useGetAccountQuery } from '../../services/accounts';
import {
  useSaveActivationPackageMutation,
  useSubmitActivationPackageMutation,
} from '../../services/activationPackage';
import { isErrorWithData } from '../../services/helpers';
import { useGetQueuesByAccountIdQuery } from '../../services/queues';
import { useGetQuotesQuery } from '../../services/quotes';
import { ActivationPackageActions } from '../../types/ActivationPackage';
import { ActionsList } from '../ActionsList';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isInternalUser } from '../auth/RequireIsInternalUser';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { ReviewSlaCountdown } from '../InstallMilestonePackage/ReviewSlaCountdown';
import { MainContainer } from '../MainContainer';
import { Footer } from '../MilestonePackagePartials/Footer';
import { MarketSpecificRequirements } from '../MilestonePackagePartials/MarketSpecificRequirements';
import { MonitoringActive } from '../MilestonePackagePartials/MonitoringActive';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { UtilityBill } from '../MilestonePackagePartials/UtilityBill';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import { QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';

import { ConditionallyApprovedDocs } from './ConditionallyApprovedDocs';
import { ExtendedWarranty } from './ExtendedWarranty';
import { InverterAndAddersInformation } from './InverterAndAddersInformation';
import { ProofOfPto } from './ProofOfPto';
import { SiteImprovement } from './SiteImprovement';

export const formatActivationPackageData = (values: FormikValues): DeepPartial<IActivationPackage> => {
  return {
    monitoring: {
      monitoringSiteId: values.monitoringSiteId,
      batteryMonitoringSiteId: values.batteryMonitoringSiteId,
    },
    ptoGrantedDate: formatPtoGrantedDate(values),
    utility: {
      tariffId: values.tariffId === '' ? null : values.tariffId,
    },
    electricianSignoffDate: values.electricianSignoffDate
      ? { palmetto: convertToISODateTimeString(values.electricianSignoffDate) }
      : undefined,
  };
};
const activationPackageSchema = yup.object().shape({
  monitoringSiteId: yup.string().required(),
});

const getMostRecentObj = (array: any[]) => {
  return array?.reduce((prev: any, current: any) => {
    return prev.date > current.date ? prev : current;
  });
};

const getSubQueueType = (programType: ProgramType): QueueType | undefined => {
  switch (programType) {
    case ProgramType.solar:
      return QueueType.permissionToOperate;
    case ProgramType.doePr:
      return QueueType.electricianSignOff;
    default:
      return undefined;
  }
};

export const ActivationPackage = ({ canSubmitActivationPackage }: { canSubmitActivationPackage: boolean }) => {
  const { ptoQueue: usePtoQueue = false, flagDrivenPackageReview = false, enableQcPriority = false } = useFlags();
  const navigate = useNavigate();
  const { id = '', action = ActivationPackageActions.review } = useParams<{
    id: string;
    action: ActivationPackageActions;
  }>();
  const allowsAction = useIsOrgReadOnly();

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id);
  const programType = account?.programType;
  const { data: quotes, isLoading: isQuotesLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType },
  );
  const { data: queuesData, isLoading: isQueueLoading } = useGetQueuesByAccountIdQuery({
    accountId: id,
    types: [
      QueueType.activationPackage,
      QueueType.installPackage,
      QueueType.permissionToOperate,
      QueueType.electricianSignOff,
    ],
  });
  const queue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.activationPackage);
  const installationQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.installPackage);
  const ptoQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.permissionToOperate);
  const electricianSignoffQueue = queuesData?.find(
    (q: QueueDocumentResponse) => q.type === QueueType.electricianSignOff,
  );
  const userPermissions = usePermissions();
  const isLightReachQCTech = userPermissions.includes(UserPermissions.lightReachQCTech);
  const [triggerSave] = useSaveActivationPackageMutation();
  const [triggerSubmit] = useSubmitActivationPackageMutation();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const triggerSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.activation);
  const showCheckboxes = isInternalUser(claims, true);
  const canSubmitReview = action === ActivationPackageActions.review && queue;
  const installConditionallyApprovedHistoryItems = installationQueue?.history?.filter(
    (item: QueueHistory) => item.changedTo === QueueStatus.conditionallyApproved,
  );
  const mostRecentInstallConditionallyApprovedHistoryItem = getMostRecentConditionallyApprovedHistoryItem(
    installConditionallyApprovedHistoryItems,
  );
  const mostRecentInstallConditionallyApprovedFlags =
    mostRecentInstallConditionallyApprovedHistoryItem?.openFlags ?? [];
  const conditionallyApprovalDate = mostRecentInstallConditionallyApprovedHistoryItem?.date;
  const conditionallyApprovedInstallFlags = useConditionallyApprovedInstallFlags({
    installationQueueFlags: installationQueue?.flags,
    mostRecentInstallConditionallyApprovedFlags,
    conditionallyApprovalDate,
  });

  const stipulationFlagsPendingToBeReviewed = conditionallyApprovedInstallFlags?.filter(
    (flag: Flag) => flag.purposeType === FlagPurposeType.stipulation && !flag.resolved,
  );
  const activeQuote = quotes?.find((quote) => quote.status === QuoteStatus.active);
  const isLoading = isQuotesLoading || isQueueLoading || isAccountLoading;
  const showUnderReviewAlert = shouldShowPackageUnderReviewAlert({
    queue,
    isFinCoUser,
  });

  const installNote = useMemo(() => {
    if (!account) {
      return;
    }
    if (
      installationQueue &&
      [QueueStatus.approved, QueueStatus.conditionallyApproved].includes(installationQueue.status)
    ) {
      return getMostRecentObj(installationQueue.approvalNotes)?.note;
    }
    return account?.notes?.milestones?.install;
  }, [account, installationQueue]);

  const isSolar = programType === ProgramType.solar;
  const isSolarPR = isSolar && account?.address?.state?.toUpperCase() === 'PR';
  const isDoePR = programType === ProgramType.doePr;
  const subQueueType = getSubQueueType(programType);
  const subQueue =
    usePtoQueue && isSolar && !isSolarPR && subQueueType === QueueType.permissionToOperate
      ? ptoQueue
      : subQueueType === QueueType.electricianSignOff
        ? electricianSignoffQueue
        : undefined;
  const subQueueNeedsApproval = subQueue
    ? ![QueueStatus.approved, QueueStatus.rejected, QueueStatus.completeHoldForApproval].includes(subQueue.status)
    : false;
  const subQueueInfo = subQueue && subQueueType ? { type: subQueueType, status: subQueue.status } : undefined;
  const showConditionallyApprovedDocs =
    installationQueue?.status === QueueStatus.conditionallyApproved &&
    (mostRecentInstallConditionallyApprovedFlags?.length > 0 ||
      (flagDrivenPackageReview && conditionallyApprovedInstallFlags?.length > 0));
  let checkboxCategories = ActivationPackageDocumentTypes;

  const canEditPackage = isPackageEditable({
    queue,
    isFinCoUser,
  });

  if (isDoePR || isSolarPR) {
    checkboxCategories = [...checkboxCategories, ActivationDocumentTypes.electricianSignOff].filter((category) => {
      return category !== ActivationDocumentTypes.ptoConfirmation;
    });
  }

  if (
    showConditionallyApprovedDocs &&
    checkboxCategories.indexOf(ActivationDocumentTypes.conditionalApproval) === -1
  ) {
    checkboxCategories.push(ActivationDocumentTypes.conditionalApproval);
  }

  const ptoIsLocked = isFinCoUser
    ? false
    : [
        QueueStatus.approved,
        QueueStatus.completeHoldForApproval,
        QueueStatus.submitted,
        QueueStatus.resubmitted,
      ].includes(ptoQueue?.status);

  const initialValues = {
    monitoringSiteId: account?.monitoringSiteId,
    batteryMonitoringSiteId: account?.batteryMonitoringSiteId,
    ptoGrantedDate: account?.ptoGrantedDate ? DateTime.fromISO(account?.ptoGrantedDate).toFormat(DATE_FORMAT) : '',
    electricianSignoffDate: account?.electricianSignoffDate?.palmetto
      ? DateTime.fromISO(account.electricianSignoffDate.palmetto).toUTC().toFormat(DATE_FORMAT)
      : '',
    lseId: account?.utility?.lseId,
    tariffId: account?.utility?.tariffId || '',
    state: account?.address?.state,
  };

  useEffect(() => {
    if (!action || !ActivationPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
    if (action === ActivationPackageActions.review && (!isFinCoUser || !isLightReachQCTech)) {
      if (queue) {
        navigate(`/accounts/${id}/activation-package/edit`, { replace: true });
      } else {
        navigate(`/accounts/${id}/activation-package/create`, { replace: true });
      }
    }
  }, [id, navigate, action, isFinCoUser, isLightReachQCTech, queue]);

  const handleSave = async ({ accountId, data }: { accountId: string; data: FormikValues }) => {
    const formattedData = formatActivationPackageData(data);
    try {
      await triggerSave({ accountId, savePackage: formattedData }).unwrap();
      triggerSuccessMessage();
      // TODO: Re-enable Hardware Lookup warnings when this behavior is required
    } catch (e) {
      console.error(e);
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        throw new Error(errorMessage);
      } else {
        throw new Error('Error saving Activation Package');
      }
    }
  };
  const handleSubmit = async (values: FormikValues, { setSubmitting }: any) => {
    try {
      const formattedData = formatActivationPackageData(values) as IActivationPackage;
      await triggerSubmit({ accountId: id, activationPackage: formattedData }).unwrap();
      toast.success('Activation Package Submitted');
      // TODO: Re-enable Hardware Lookup warnings when this behavior is required
      navigate(`/accounts/${id}`);
    } catch (e: any) {
      console.error(e);
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(errorMessage);
      } else {
        toast.error('Error saving Activation Package');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidation = (values: any) => {
    const errors = {} as any;
    const inverterManufacturer =
      account?.systemDesign?.inverters?.[0]?.manufacturer || account?.systemDesign?.inverterManufacturer;
    const monitoringIdFormat = validateMonitoringIdFormat(values.monitoringSiteId, inverterManufacturer);
    if (values.monitoringSiteId && !monitoringIdFormat.valid) {
      errors.monitoringSiteId = `Monitoring Site ID must match the required format: ${monitoringIdFormat.expectedFormat}.`;
    }
    if (isSolar && !isSolarPR) {
      if (!values.ptoGrantedDate) {
        errors.ptoGrantedDate = 'PTO Granted Date is required';
      } else if (!validateDate(values.ptoGrantedDate)) {
        errors.ptoGrantedDate = 'PTO Granted Date is invalid. Must be in the format MM-DD-YYYY';
      }
    }
    if (isDoePR || isSolarPR) {
      if (!values.electricianSignoffDate) {
        errors.electricianSignoffDate = 'Electrician Sign Off Date is required';
      }
    }
    return errors;
  };

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }

  return (
    <QueueFlagsProvider queueId={queue?.id}>
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.activationPackage} />
      <Helmet>
        <title>{`${account?.primaryApplicantName} Activation Package`}</title>
      </Helmet>
      <MainContainer>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          validateOnChange={false}
          validate={handleValidation}
          validationSchema={activationPackageSchema}
        >
          <EnhancedFormikForm>
            {enableQcPriority && queue?.reviewSla?.current?.targetDate && !queue.reviewSla.current.decisionDate && (
              <Box justifyContent="flex-end" gap="sm" padding="0 0 md 0">
                {isFinCoUser && queue?.reviewSla?.reviewPriorityTier != null && (
                  <Box fontSize="sm" fontWeight="medium" alignItems="flex-end">
                    Tier: {queue.reviewSla.reviewPriorityTier}
                  </Box>
                )}
                <ReviewSlaCountdown targetDate={queue.reviewSla.current.targetDate} />
              </Box>
            )}
            <ActionsList
              milestoneType={MilestoneType.activation}
              accountId={queue?.account?.id}
              canSubmitPackage={allowsAction && canSubmitActivationPackage}
              disableApproval={subQueueNeedsApproval}
              isLoading={isLoading}
              isFirstSubmission={!queue}
              canSubmitReview={canSubmitReview}
              onSave={handleSave}
              disabled={!allowsAction || !canEditPackage}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              conditionallyApprovedFlags={stipulationFlagsPendingToBeReviewed}
              queue={queue}
              subQueueInfo={subQueueInfo}
            />
            {queue?.status === QueueStatus.rejected || isFinCoUser ? (
              <QueueBanner
                setIsNotesOpen={setIsNotesOpen}
                queueType={QueueType.activationPackage}
                isFinCoUser={isFinCoUser}
                is8090Project={Boolean(queue?.aiPackageReviewStatus)}
              />
            ) : null}
            {showUnderReviewAlert ? (
              <Alert
                variant="warning"
                title="Activation Package In Review Queue"
                message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                hasIcon
              />
            ) : null}
            {action === ActivationPackageActions.review && !canSubmitReview ? (
              <Alert
                variant="warning"
                title="Activation Package Not Submitted"
                message="This activation package is not ready to be reviewed yet. It has not been submitted."
                hasIcon
              />
            ) : null}
            {queue?.status !== QueueStatus.rejected && installNote && (
              <Alert variant="info" hasIcon title="Install Notes" message={installNote} />
            )}
            <InverterAndAddersInformation />
            {showConditionallyApprovedDocs && (
              <ConditionallyApprovedDocs
                flaggedDocs={conditionallyApprovedInstallFlags}
                queue={queue}
                showCheckboxes={showCheckboxes}
                checkboxCategories={checkboxCategories}
                isPackageEditable={canEditPackage}
              />
            )}
            <MonitoringActive
              adders={activeQuote?.adders}
              accountId={id}
              milestoneType={MilestoneType.activation}
              isPackageEditable={canEditPackage}
            />
            {!isDoePR && !isSolarPR && (
              <ProofOfPto
                disabled={ptoIsLocked}
                showCheckboxes={showCheckboxes}
                checkboxCategories={checkboxCategories}
                isReviewMode={action === ActivationPackageActions.review}
                isPackageEditable={canEditPackage}
              />
            )}
            {(isDoePR || isSolarPR) && (
              <ElectricianSignOff
                showCheckboxes={showCheckboxes}
                checkboxCategories={checkboxCategories}
                isPackageEditable={canEditPackage}
              />
            )}
            <UtilityBill
              account={account}
              originContext={QueueType.activationPackage}
              showCheckboxes={showCheckboxes}
              checkboxCategories={checkboxCategories}
              isPackageEditable={canEditPackage}
            />
            <MarketSpecificRequirements
              originContext={QueueType.activationPackage}
              showCheckboxes={showCheckboxes}
              checkboxCategories={checkboxCategories}
              milestoneType={MilestoneType.activation}
              isPackageEditable={canEditPackage}
            />
            <ExtendedWarranty
              originContext={QueueType.activationPackage}
              showCheckboxes={showCheckboxes}
              checkboxCategories={checkboxCategories}
              isPackageEditable={canEditPackage}
            />
            <SiteImprovement
              showCheckboxes={showCheckboxes}
              checkboxCategories={checkboxCategories}
              programType={programType}
              isPackageEditable={canEditPackage}
            />
            <Footer
              canSubmitPackage={allowsAction && canSubmitActivationPackage}
              disableApproval={subQueueNeedsApproval}
              isLoading={isLoading}
              isFirstSubmission={!queue}
              canSubmitReview={canSubmitReview}
              onSave={handleSave}
              disabled={!allowsAction || !canEditPackage}
              conditionallyApprovedFlags={stipulationFlagsPendingToBeReviewed}
              subQueueInfo={subQueueInfo}
            />
          </EnhancedFormikForm>
        </Formik>
      </MainContainer>
    </QueueFlagsProvider>
  );
};
